import { queryPredicateByKey } from '@api/query-client';
import { useQueryClient } from '@tanstack/react-query';
import {
  EXTERNAL_ACCOUNT,
  PORTFOLIO,
  WATCHLIST
} from '@unistocks/features/portfolios/portfolio-type';
import { invalidatePortfolioQueriesById } from '@unistocks/features/portfolios/portfolio-utils';
import {
  externalAccountsGetAccountQueryKey,
  externalAccountsGetAccountsQueryKey,
  externalAccountsGetPortfoliosQueryKey
} from '@unistocks/proxy/axios-client/ExternalAccountsQuery';
import {
  watchlistsGetListQueryKey,
  watchlistsGetQueryKey
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { Logger } from '@unistocks/shared/services/logger.service';
import {
  PORTFOLIO_HUB,
  RECEIVE_PORTFOLIO_EVENT,
  RECEIVE_SYNC_EVENT
} from '@unistocks/shared/signalr/consts';
import { SignalREvent, SyncEvent } from '@unistocks/shared/signalr/signalr-event';
import { useSignalRHub } from '@unistocks/shared/signalr/use-signalr-hub';
import { createDate } from '@unistocks/shared/utils/util';
import { useCallback, useEffect } from 'react';

/**
 * Hook that listens to portfolio events from the SignalR hub
 * and invalidates related queries.
 */
export const usePortfolioHub = () => {
  const connection = useSignalRHub(PORTFOLIO_HUB);
  const queryClient = useQueryClient();

  const handleEvent = useCallback(
    (event: SignalREvent) => {
      const { type, entity, id } = event;
      Logger.debug('Received event', type, entity, id ?? '');

      // Invalidate related queries based on the event id
      queryClient.invalidateQueries({
        predicate: query => {
          const isRelatedQuery = queryPredicateByKey(id)(query);
          // If the query was fetched less than 30 seconds ago, skip invalidation
          const isStale =
            !query.state.dataUpdatedAt ||
            createDate(query.state.dataUpdatedAt).diffNow('seconds').seconds < -30;

          return isRelatedQuery && isStale;
        }
      });

      // Invalidate related queries based on the entity type
      switch (entity) {
        case PORTFOLIO:
          // Invalidate portfolio
          invalidatePortfolioQueriesById(queryClient, id);
          // Invalidate all dashboard data
          queryClient.invalidateQueries({
            // TODO: Is there a better way to invalidate all dashboard data queries?
            predicate: query =>
              query.queryKey.some(
                key => typeof key === 'string' && key.includes('dashboard') && key.includes('data')
              )
          });
          break;

        case WATCHLIST:
          queryClient.invalidateQueries({ queryKey: watchlistsGetQueryKey(id) });
          queryClient.invalidateQueries({ queryKey: watchlistsGetListQueryKey() });
          break;

        case EXTERNAL_ACCOUNT:
          const { provider } = event as SyncEvent;
          queryClient.invalidateQueries({ queryKey: externalAccountsGetAccountsQueryKey() });
          queryClient.invalidateQueries({
            queryKey: externalAccountsGetAccountQueryKey(provider, id)
          });
          queryClient.invalidateQueries({
            queryKey: externalAccountsGetPortfoliosQueryKey(provider, id)
          });
          break;
      }
    },
    [queryClient]
  );

  useEffect(() => {
    connection?.on(RECEIVE_PORTFOLIO_EVENT, handleEvent);
    connection?.on(RECEIVE_SYNC_EVENT, handleEvent);

    return () => {
      connection?.off(RECEIVE_PORTFOLIO_EVENT);
      connection?.off(RECEIVE_SYNC_EVENT);
    };
  }, [connection, handleEvent, queryClient]);
};
